import { useEleveGroups } from 'queries/eleveGroups/useEleveGroups';
import { format } from '@teetsh/app/src/utils/time/format';
import { useEphemeralEleveGroups } from 'queries/eleveGroups/useEphemeralEleveGroups';
import { CJAtelier } from 'types/cj';
import { Eleve } from 'types/eleves';
import { EleveGroup, BasicEleveInfo } from 'types/eleveGroups';
import { getAllPendingEphemeralGroups } from './getAllPendingEphemeralGroups';

interface Props {
  start: Date;
  end: Date;
  ateliers: CJAtelier[];
  eleves: Eleve[];
}

export function useGroups(props: Props) {
  const { start, end, ateliers, eleves } = props;

  // Load regular groups from server
  const { data: regularGroups = [] } = useEleveGroups();

  // Load ephemeral groups for the date range
  const fromDate = start ? format(start, 'fr-FR', 'yyyy-MM-dd') : '';
  const toDate = end ? format(end, 'fr-FR', 'yyyy-MM-dd') : '';
  const { data: ephemeralGroups = [] } = useEphemeralEleveGroups({
    from: fromDate,
    to: toDate,
  });

  const allPendingGroups = getAllPendingEphemeralGroups(ateliers);

  // Filter out pending groups that already exist in saved ephemeralGroups
  const ephemeralGroupIds = new Set(ephemeralGroups.map(g => g.id));
  const uniquePendingGroups = allPendingGroups.filter(
    peg => !ephemeralGroupIds.has(peg.id),
  );

  // Convert pending ephemeral groups to EleveGroup objects
  const pendingGroups: EleveGroup[] = uniquePendingGroups.map(peg => {
    // Map elevesIds to actual eleve objects
    const groupEleves: BasicEleveInfo[] = [];
    peg.elevesIds.forEach(id => {
      const eleve = eleves.find(e => e.id === id);
      if (eleve) {
        groupEleves.push({
          id: eleve.id,
          givenName: eleve.givenName,
          familyName: eleve.familyName,
          picture: eleve.picture,
        });
      }
    });

    return {
      id: peg.id,
      name: 'N/A',
      color: 'gray-200' as const,
      eleves: groupEleves,
      isEphemeral: true,
    };
  });

  // Combine groups that are used
  const usedEphemeralGroupIds = new Set<string>();
  ateliers.forEach(atelier => {
    atelier.groupPivots?.forEach(gp => {
      if (ephemeralGroups.some(eg => eg.id === gp.id)) {
        usedEphemeralGroupIds.add(gp.id);
      }
    });
  });

  const eleveGroups = [
    ...regularGroups,
    ...ephemeralGroups.filter(g => usedEphemeralGroupIds.has(g.id)),
    ...pendingGroups,
  ];

  // Include pending groups in ephemeralGroups since they ARE ephemeral (just not saved yet)
  const allEphemeralGroups = [...ephemeralGroups, ...pendingGroups];

  return {
    eleveGroups,
    regularGroups,
    ephemeralGroups: allEphemeralGroups,
    savedEphemeralGroups: ephemeralGroups, // Original saved ephemeral groups from database
  };
}
